import React, { useCallback, useRef, useState } from 'react';

type RenderItemProps<T, E extends HTMLElement> = {
  key: string;
  data: T;
  elementRef: (el: E | null) => void;
  style: React.CSSProperties;
  slideOut: (id: string) => void;
};

type BaseItem = {
  id: string;
};

type SlideOutListProps<T extends BaseItem, E extends HTMLElement> = {
  initialList: T[];
  renderItem: (props: RenderItemProps<T, E>) => JSX.Element;
  renderEmptyList?: () => JSX.Element;
  onRemoveElement: (id: string) => void;
};

export const SlideOutList = <T extends BaseItem, E extends HTMLElement>({
  initialList,
  renderItem,
  renderEmptyList,
  onRemoveElement,
}: SlideOutListProps<T, E>): JSX.Element => {
  const [hiddenItems, setHiddenItems] = useState<Set<string>>(new Set());
  const [slidingOut, setSlidingOut] = useState<string | null>(null);
  const refs = useRef<{ [key: string]: E | null }>({});

  const slideOut = useCallback(
    (id: string): void => {
      setSlidingOut(id);
      setTimeout(() => {
        setHiddenItems((prevHiddenItems) => {
          const newSet = new Set(prevHiddenItems);
          newSet.add(id);
          return newSet;
        });
        setSlidingOut(null);
        onRemoveElement(id);
      }, 350);
    },
    [onRemoveElement],
  );

  const getItemStyle = useCallback(
    (id: string): React.CSSProperties => {
      const isSlidingOut = slidingOut === id;

      return {
        transition:
          'transform 0.35s ease-in-out, opacity 0.35s ease-in-out, height 0.35s ease-in-out, margin 0.35s ease-in-out',
        transform: isSlidingOut ? 'translateY(-30px)' : 'unset',
        opacity: isSlidingOut ? 0 : 1,
        overflow: 'hidden',
        margin: isSlidingOut ? '0px' : '10px 0',
      };
    },
    [slidingOut],
  );

  const visibleItems = initialList.filter((item) => !hiddenItems.has(item.id));

  if (visibleItems.length === 0 && renderEmptyList) {
    return renderEmptyList();
  }

  return (
    <>
      {visibleItems.map((item) =>
        renderItem({
          key: item.id,
          data: item,
          elementRef: (el) => (refs.current[item.id] = el as E | null),
          style: getItemStyle(item.id),
          slideOut: slideOut,
        }),
      )}
    </>
  );
};
